import * as autobind from 'autobind';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'redux-scaffolding-ts';
import { Input, Icon, Message, Grid } from 'semantic-ui-react';
import { TypeHoursCodeDto, TypeHoursCodeStore } from 'stores/configuration/events-n-requests/type-hour-code-store';
import { ItemState, OrderDefinition, Query } from 'stores/dataStore';
import { CommandResult } from 'stores/types';
import { nameof } from 'utils/object';
import { TableModel, TableView } from 'widgets/collections/table';
import { TextBoxFilter } from 'widgets/collections/table-filters/textbox-filter';

import NewTypeHoursCodeView from './new-type-hours-code';
import ChangeTypeHoursCodeView from './edit-type-hours-code';

export interface TypeHoursCodeListProps extends WithTranslation, RouteComponentProps {
  typeHoursCode: TypeHoursCodeStore;
}

export interface TypeHoursCodeListState {
  query: Query;
  newTypeHoursCodeShown: boolean;
  changeTypeHoursCodeShown: boolean;
  activeFilters: string[];
  selectedItem: any;
  someFilterOpened: boolean;
}

@connect(['typeHoursCode', TypeHoursCodeStore])
class TypeHoursCodeListPage extends React.Component<TypeHoursCodeListProps, TypeHoursCodeListState> {
  handleOnEnterKeydown = () => {
    this.onEditItem();
  };

  handleOnRowDoubleClick = () => {
    this.onEditItem();
  };

  constructor(props) {
    super(props);
    this.state = {
      query: { searchQuery: '', orderBy: [{ direction: 'Descending', field: 'modifiedOn', useProfile: false }], skip: 0, take: 10 },
      newTypeHoursCodeShown: false,
      changeTypeHoursCodeShown: false,
      activeFilters: [],
      selectedItem: null,
      someFilterOpened: false
    };
  }

  componentDidMount() {
    this.load();
  }

  @autobind
  private load() {
    this.props.typeHoursCode.getAllAsync(this.state.query);
  }

  @autobind
  private handleOrderBy(orderBy: OrderDefinition[]) {
    this.setState(
      {
        query: Object.assign(this.state.query, {
          orderBy: [...orderBy, { direction: 'Descending', field: 'modifiedOn', useProfile: false }]
        })
      },
      this.load
    );
  }

  @autobind
  private handlePageChange(skip: number, take: number) {
    this.setState({ query: Object.assign(this.state.query, { skip, take }) });
    this.load();
  }

  @autobind
  private async onSaveRow(item: TypeHoursCodeDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.typeHoursCode.saveAsync(item, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private async onDelete(item: TypeHoursCodeDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.typeHoursCode.deleteAsync(item.id, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private handleFilterChange(filters: { id: string; filter: any }[]) {
    const filter = filters.map(f => f.filter);
    const activeFilters = filters.map(f => f.id);

    const query = Object.assign(this.state.query, { filter, skip: 0 });
    this.setState({ query, activeFilters }, () => this.load());
  }

  @autobind
  private onNewItem() {
    this.setState({ newTypeHoursCodeShown: true });
  }

  @autobind
  private onNewItemClosed(isSuccess: boolean) {
    this.setState({ newTypeHoursCodeShown: false });
    if (isSuccess) this.load();
  }

  @autobind
  private onEditItem() {
    this.setState({ changeTypeHoursCodeShown: true });
  }

  @autobind
  private onEditItemClosed(isSuccess: boolean) {
    this.setState({ changeTypeHoursCodeShown: false });
    if (isSuccess) this.load();
  }

  handleOnActivateFilter = (visible: boolean) => {
    this.setState({ someFilterOpened: visible });
  };

  public render() {
    const { t } = this.props as any;
    const { activeFilters, someFilterOpened } = this.state;

    const tableModel = {
      columns: [
        {
          title: t('Name'),
          newClassFromComponent: data => 'table__more-text-long',
          tooltipRenderer: false,
          renderer: data => data.code,
          editor: (data, onChange) => (
            <Input
              value={data.code}
              fluid
              onChange={(e, { value }) => {
                data.code = value;
                onChange();
              }}
            />
          ),
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by name')}
              triggerTitle={title}
              onFilter={value => onFilter(nameof<TypeHoursCodeDto>('code'), `contains(${nameof<TypeHoursCodeDto>('code')}, '${value}')`)}
              onClear={() => onClear(nameof<TypeHoursCodeDto>('code'))}
              active={activeFilters.includes(nameof<TypeHoursCodeDto>('code'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: nameof<TypeHoursCodeDto>('code'),
            useProfile: false,
            active: 'Ascending'
          }
        }
      ],
      data: this.props.typeHoursCode.state
    } as TableModel<TypeHoursCodeDto>;
    return (
      <>
        <Grid className="event-types-list-grid">
          {this.props.typeHoursCode.state.result && !this.props.typeHoursCode.state.result.isSuccess && (
            <Grid.Row className="event-types-list-error-row">
              <Message
                className="error-message__style"
                icon="exclamation circle"
                error
                header={t('An error ocurred')}
                list={this.props.typeHoursCode.state.result.messages.map(o => o.body)}
              />
            </Grid.Row>
          )}
          <Grid.Row className="event-types-list-items-row request-list__table-view">
            <TableView
              /////////////////For build table keyboard navegation/////////////////
              selectable={!this.state.newTypeHoursCodeShown && !this.state.changeTypeHoursCodeShown}
              maxSelection={1}
              onHideCheckbox={true}
              selectionType={'allRow'}
              onEnterKeydown={this.handleOnEnterKeydown}
              onRowDoubleClick={this.handleOnRowDoubleClick}
              preventEnterKeyDownEvent={someFilterOpened}
              //showActionsConfirmModal={true}
              /////////////////For build table keyboard navegation/////////////////
              model={tableModel}
              extraActions={[
                {
                  content: (
                    <>
                      <Icon name="edit" />
                      &nbsp;{t('Edit')}
                    </>
                  ),
                  onClick: item => {
                    this.setState({ selectedItem: item }, () => this.onEditItem());
                  }
                }
              ]}
              onOrderByChanged={this.handleOrderBy}
              onNewItem={this.onNewItem}
              onRefresh={this.load}
              canEdit={false}
              canDelete={true}
              onDeleteRow={this.onDelete}
              onSaveRow={this.onSaveRow}
              onPageChange={this.handlePageChange}
              onFilterChange={this.handleFilterChange}
              canCreateNew={true}
              createNewButtonTitle={t('Add Type Hours Code')}
            ></TableView>
          </Grid.Row>
        </Grid>
        {(this.state.newTypeHoursCodeShown && <NewTypeHoursCodeView onClose={this.onNewItemClosed} {...this.props} />) ||
          (this.state.changeTypeHoursCodeShown && (
            <ChangeTypeHoursCodeView onClose={this.onEditItemClosed} currentTypeHoursCode={this.state.selectedItem} />
          ))}
      </>
    );
  }
}
export default withTranslation()(TypeHoursCodeListPage);
